import { useCallback, useState } from 'react';

/**
 * Hell oder dunkel — die Wahl des Anwenders, gemerkt im Browser.
 *
 * **Zwei Zustände und keinen dritten.** `hell` ist die Voreinstellung und sieht
 * aus wie die Oberfläche vor dieser Änderung; `dunkel` ist die zweite Fassung
 * derselben Oberfläche. Ein dritter Zustand «folge dem System» klingt gefällig
 * und kostet eine Fassung, die niemand prüft — die Begründung steht in
 * `tailwind.config.js`.
 *
 * **Die Klasse hängt am `<html>`-Element**, nicht an einem Rahmen im Baum von
 * React. Nur so greift sie auch dort, wo React nicht hinkommt: Der Hintergrund
 * des Fensters selbst, die Auswahlfarbe, ein modaler Dialog in einem Portal.
 *
 * **Gesetzt wird sie zuerst von einem Inline-Skript in `index.html`** — vor dem
 * ersten Anstrich. Dieses Modul schaltet sie danach um; die Voreinstellung zu
 * lesen ist beiden gemeinsam, und deshalb steht der Schlüsselname an zwei
 * Stellen. Wer ihn hier ändert, ändert ihn dort mit.
 */
export type Farbschema = 'hell' | 'dunkel';

/**
 * Der Schlüssel im lokalen Speicher.
 *
 * Derselbe Name steht wörtlich im Inline-Skript von `index.html`. Der Punkt im
 * Namen ist Absicht: Der lokale Speicher gehört der Herkunft und nicht dieser
 * Anwendung allein, und ein Schlüssel `farbschema` ohne Vorsatz wäre eine
 * Wette darauf, dass unter derselben Adresse nie etwas anderes läuft.
 */
export const FARBSCHEMA_SCHLUESSEL = 'fameacc.farbschema';

/**
 * Das gemerkte Schema, oder `hell`.
 *
 * Jeder Wert, der nicht wörtlich `dunkel` ist, gilt als `hell`. Damit ist ein
 * verstümmelter Eintrag — von Hand gesetzt, von einer älteren Fassung
 * geschrieben — kein Fehlerfall, sondern die Voreinstellung.
 */
export function gemerktesFarbschema(): Farbschema {
  try {
    return window.localStorage.getItem(FARBSCHEMA_SCHLUESSEL) === 'dunkel' ? 'dunkel' : 'hell';
  } catch {
    // Privates Fenster mit abgeschaltetem Speicher: `localStorage` wirft schon
    // beim Lesen. Die Oberfläche bleibt hell und funktioniert.
    return 'hell';
  }
}

/** Setzt die Klasse `dark` am `<html>`-Element — oder nimmt sie weg. */
export function farbschemaAnwenden(schema: Farbschema): void {
  document.documentElement.classList.toggle('dark', schema === 'dunkel');
}

/** Merkt das Schema. Ein fehlgeschlagenes Merken ist kein Grund, nicht umzuschalten. */
function farbschemaMerken(schema: Farbschema): void {
  try {
    window.localStorage.setItem(FARBSCHEMA_SCHLUESSEL, schema);
  } catch {
    // Kein Speicher: Die Wahl gilt für diese Sitzung und ist beim nächsten
    // Laden weg. Das ist besser als ein Umschalter, der nichts tut.
  }
}

/** Das aktuelle Schema samt Umschalter. */
export interface FarbschemaSteuerung {
  readonly schema: Farbschema;
  readonly umschalten: () => void;
}

/**
 * Liest das gemerkte Schema und schaltet es um.
 *
 * **Ohne Effekt beim Einhängen, und das ist Absicht.** Die Klasse steht schon —
 * das Inline-Skript hat sie vor dem ersten Anstrich gesetzt. Ein Effekt, der
 * sie erneut setzte, wäre wirkungslos; einer, der den Wert erneut merkte,
 * schriebe bei jedem Laden in den Speicher, ohne dass jemand etwas gewählt
 * hätte.
 */
export function useFarbschema(): FarbschemaSteuerung {
  const [schema, setSchema] = useState<Farbschema>(gemerktesFarbschema);

  const umschalten = useCallback((): void => {
    const nachher: Farbschema = schema === 'dunkel' ? 'hell' : 'dunkel';
    farbschemaAnwenden(nachher);
    farbschemaMerken(nachher);
    setSchema(nachher);
  }, [schema]);

  return { schema, umschalten };
}
